import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { invalidateDataUploadFiles } from './file-uploads/invalidateDataUploadFiles';
import { UploadedFile } from './file-uploads/types';

type MutationInput = {
  clientId: string;
  dataUploadFileId: string;
  documentDate: Date;
  isResolved: boolean;
  parentId: string | undefined;
};

type MutationContext = {
  queryKey: string[];
  previousFiles: UploadedFile[] | undefined;
};

export function useUpdateFileDocumentDate() {
  const queryClient = useQueryClient();
  const { clients } = useCurrentClientIfAvailable();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<unknown, Error, MutationInput, MutationContext>({
    mutationFn: ({
      clientId,
      dataUploadFileId,
      documentDate,
      isResolved,
      parentId,
    }: MutationInput) =>
      api.put(`clients/${clientId}/data-upload-files/${dataUploadFileId}`, {
        documentDate: documentDate.toISOString().split('T')[0],
        isResolved,
        parentId: parentId ?? null,
        isFiled: false,
      }),
    onMutate: async ({ clientId, dataUploadFileId, documentDate }) => {
      const queryKey = ['clients', clientId, 'data-upload-files'];
      await queryClient.cancelQueries({ queryKey });
      const previousFiles = queryClient.getQueryData<UploadedFile[]>(queryKey);
      const nextDocumentDate = documentDate.toISOString().split('T')[0];
      queryClient.setQueryData<UploadedFile[]>(queryKey, current =>
        current?.map(file =>
          file.id === dataUploadFileId ? { ...file, documentDate: nextDocumentDate } : file,
        ),
      );
      return { queryKey, previousFiles };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousFiles);
      }
    },
    onSettled: (_data, _error, variables) => {
      const client = clients.find(client => client.id === variables.clientId);
      return invalidateDataUploadFiles(queryClient, {
        clientId: variables.clientId,
        familyId: client?.family?.id ?? client?.familyId,
      });
    },
  });

  const updateDocumentDate = async (
    clientId: string,
    dataUploadFileId: string,
    documentDate: Date,
    isResolved: boolean,
    parentId: string | undefined,
  ) => {
    return mutateAsync({ clientId, dataUploadFileId, documentDate, isResolved, parentId });
  };

  return {
    updateDocumentDate,
    error,
    isLoading,
    reset,
  };
}
